import { useState, useEffect } from 'react';
import { Users, Plus, Edit, Trash2, Eye, Filter } from 'lucide-react';

interface Segment {
  id: string;
  name: string;
  description: string;
  filters: Record<string, any>;
  created_at: string;
}

interface PreviewData {
  total: number;
  users: Array<{
    id: string;
    name: string;
    email: string;
    phone: string;
  }>;
}

export default function CampaignSegments() {
  const [segments, setSegments] = useState<Segment[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingSegment, setEditingSegment] = useState<Segment | null>(null);
  const [previewData, setPreviewData] = useState<PreviewData | null>(null);
  const [showPreview, setShowPreview] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    filters: {
      bot_enabled: '',
      seller_id: ''
    },
    manual_emails: '' // Emails separados por vírgula ou linha
  });

  useEffect(() => {
    loadSegments();
  }, []);

  const loadSegments = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${import.meta.env.VITE_API_URL || window.location.origin}/api/campaign-segments`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setSegments(data.data);
      }
    } catch (error) {
      console.error('Error loading segments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || window.location.origin;
      
      // Limpar filtros vazios
      const cleanFilters: Record<string, any> = {};
      Object.entries(formData.filters).forEach(([key, value]) => {
        if (value !== '') {
          cleanFilters[key] = value === 'null' ? null : value === 'true' ? true : value === 'false' ? false : value;
        }
      });

      const method = editingSegment ? 'PUT' : 'POST';
      const url = editingSegment 
        ? `${apiUrl}/api/campaign-segments/${editingSegment.id}`
        : `${apiUrl}/api/campaign-segments`;

      // Adicionar emails manuais aos filtros se fornecidos
      if (formData.manual_emails.trim()) {
        cleanFilters.manual_emails = formData.manual_emails.trim();
      }

      const res = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          description: formData.description,
          filters: cleanFilters
        })
      });

      const data = await res.json();
      
      if (data.success) {
        alert(editingSegment ? 'Segmento atualizado!' : 'Segmento criado!');
        setShowModal(false);
        resetForm();
        loadSegments();
      } else {
        alert('Erro: ' + data.error);
      }
    } catch (error) {
      console.error('Error saving segment:', error);
      alert('Erro ao salvar segmento');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Tem certeza que deseja deletar este segmento?')) return;

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${import.meta.env.VITE_API_URL || window.location.origin}/api/campaign-segments/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await res.json();
      if (data.success) {
        alert('Segmento deletado!');
        loadSegments();
      }
    } catch (error) {
      console.error('Error deleting segment:', error);
      alert('Erro ao deletar segmento');
    }
  };

  const handlePreview = async (id: string) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${import.meta.env.VITE_API_URL || window.location.origin}/api/campaign-segments/${id}/preview`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await res.json();
      if (data.success) {
        setPreviewData(data.data);
        setShowPreview(true);
      }
    } catch (error) {
      console.error('Error previewing segment:', error);
      alert('Erro ao visualizar preview');
    }
  };

  const handleEdit = (segment: Segment) => {
    setEditingSegment(segment);
    setFormData({
      name: segment.name,
      description: segment.description || '',
      filters: {
        bot_enabled: segment.filters.bot_enabled !== undefined ? String(segment.filters.bot_enabled) : '',
        seller_id: segment.filters.seller_id !== undefined ? (segment.filters.seller_id === null ? 'null' : segment.filters.seller_id) : ''
      },
      manual_emails: (segment.filters as any).manual_emails || ''
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      filters: {
        bot_enabled: '',
        seller_id: ''
      },
      manual_emails: ''
    });
    setEditingSegment(null);
  };

  if (loading) {
    return <div className="p-8">Carregando...</div>;
  }

  return (
    <div className="p-8">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Segmentos de Campanha</h1>
          <p className="text-gray-600">Gerencie os públicos-alvo das suas campanhas</p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="h-5 w-5 mr-2" />
          Novo Segmento
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {segments.map((segment) => (
          <div key={segment.id} className="bg-white rounded-lg shadow p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center">
                <Users className="h-5 w-5 text-blue-600 mr-2" />
                <h3 className="font-semibold text-gray-900">{segment.name}</h3>
              </div>
            </div>

            {segment.description && (
              <p className="text-sm text-gray-600 mb-4">{segment.description}</p>
            )}

            <div className="mb-4">
              <div className="flex items-center text-sm text-gray-500 mb-2">
                <Filter className="h-4 w-4 mr-1" />
                Filtros:
              </div>
              <div className="bg-gray-50 rounded p-2 text-xs font-mono">
                {JSON.stringify(segment.filters, null, 2)}
              </div>
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => handlePreview(segment.id)}
                className="flex-1 flex items-center justify-center px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
              >
                <Eye className="h-4 w-4 mr-1" />
                Preview
              </button>
              <button
                onClick={() => handleEdit(segment)}
                className="flex items-center justify-center px-3 py-2 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
              >
                <Edit className="h-4 w-4" />
              </button>
              <button
                onClick={() => handleDelete(segment.id)}
                className="flex items-center justify-center px-3 py-2 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Modal de Criar/Editar */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl">
            <h2 className="text-xl font-bold mb-4">
              {editingSegment ? 'Editar Segmento' : 'Novo Segmento'}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nome *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Descrição
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  rows={3}
                />
              </div>

              <div className="border-t pt-4">
                <h3 className="font-semibold mb-3">Filtros</h3>
                
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Bot Ativo
                    </label>
                    <select
                      value={formData.filters.bot_enabled}
                      onChange={(e) => setFormData({
                        ...formData,
                        filters: { ...formData.filters, bot_enabled: e.target.value }
                      })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                    >
                      <option value="">Todos</option>
                      <option value="true">Sim (Bot Ativo)</option>
                      <option value="false">Não (Atendimento Manual)</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Atendente
                    </label>
                    <select
                      value={formData.filters.seller_id}
                      onChange={(e) => setFormData({
                        ...formData,
                        filters: { ...formData.filters, seller_id: e.target.value }
                      })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                    >
                      <option value="">Todos</option>
                      <option value="null">Sem Atendente</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      Para filtrar por atendente específico, adicione o ID manualmente
                    </p>
                  </div>
                </div>
              </div>

              <div className="border-t pt-4">
                <h3 className="font-semibold mb-3">📧 Emails Manuais (Opcional)</h3>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Lista de Emails
                  </label>
                  <textarea
                    value={formData.manual_emails}
                    onChange={(e) => setFormData({ ...formData, manual_emails: e.target.value })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 font-mono text-sm"
                    rows={6}
                    placeholder="Digite os emails, um por linha ou separados por vírgula:&#10;exemplo1@email.com&#10;exemplo2@email.com, exemplo3@email.com"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    💡 Cole uma lista de emails separados por vírgula ou quebra de linha. 
                    Se preenchido, <strong>APENAS estes emails</strong> receberão a campanha (ignora outros filtros).
                  </p>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {editingSegment ? 'Atualizar' : 'Criar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Preview */}
      {showPreview && previewData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[80vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">
              Preview do Segmento ({previewData.total} usuários)
            </h2>

            <div className="space-y-2">
              {previewData.users.map((user) => (
                <div key={user.id} className="border rounded p-3 flex justify-between items-center">
                  <div>
                    <div className="font-medium">{user.name}</div>
                    <div className="text-sm text-gray-600">{user.email}</div>
                  </div>
                  <div className="text-sm text-gray-500">{user.phone}</div>
                </div>
              ))}
            </div>

            <div className="flex justify-end mt-6">
              <button
                onClick={() => setShowPreview(false)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
